import { Chunk } from 'chunkosaurus';
import {
  VisitorInteractionUpdate,
  RrwebEventAcksSerializeable,
  ChatMessageArgs,
  ChatMessage,
  ChatMessageErrorCode,
  PartialConversation,
  PartialVisitor,
  VisitorUpdates,
  WidgetVisitor,
  Visitor,
  WidgetOrg,
  WidgetInitData,
  VisitorJourney,
  VisitorIdentity,
  Agent,
  VisitorSession,
  SessionInteraction,
  UniqueVisitor,
  JourneyTestResult,
  SalesfloorChatMessage,
} from '.';
import { AiCobrowsingAction, AiCobrowsingMessageArgs } from './ai-cobrowsing-types';
import { AiDemoSession } from './ai-demo-types';

export interface ServerRrwebData {
  agentSid: string;
  content: Chunk;
  clientSid: string;
}

export enum PubsubChannelType {
  // Frontend
  'chat.inbox.unread-message-count.user' = 'chat.inbox.unread-message-count.user',
  'chat.inbox.stats.org' = 'chat.inbox.stats.org',
  'chat.inbox.stats.user' = 'chat.inbox.stats.user',
  'chat.inbox.stats.group' = 'chat.inbox.stats.group',
  'chat.inbox.conversation-updates.open.assigned' = 'chat.inbox.conversation-updates.open.assigned',
  'chat.inbox.conversation-updates.open.mentioned' = 'chat.inbox.conversation-updates.open.mentioned',
  'chat.inbox.conversation-updates.open.all' = 'chat.inbox.conversation-updates.open.all',
  'chat.inbox.conversation-updates.open.unassigned' = 'chat.inbox.conversation-updates.open.unassigned',
  'chat.inbox.conversation-updates.closed.assigned' = 'chat.inbox.conversation-updates.closed.assigned',
  'chat.inbox.conversation-updates.closed.mentioned' = 'chat.inbox.conversation-updates.closed.mentioned',
  'chat.inbox.conversation-updates.closed.all' = 'chat.inbox.conversation-updates.closed.all',
  'chat.inbox.conversation-updates.closed.unassigned' = 'chat.inbox.conversation-updates.closed.unassigned',
  'chat.conversation' = 'chat.conversation',
  'chat.conversations.visitor' = 'chat.conversations.visitor',
  'app-notifications.user' = 'app-notifications.user',
  'salesfloor.room' = 'salesfloor.room',
  'salesfloor.conversation.room' = 'salesfloor.conversation.room',
  'salesfloor.conversation.dm' = 'salesfloor.conversation.dm',
  'livelisten.dial_session.new' = 'livelisten.dial_session.new',
  'livelisten.dial_session.ended' = 'livelisten.dial_session.ended',
  'ai-chatbot.faq.generated_answer' = 'ai_chatbot.faq.generated_answer',
  // Widget
  'chat.widget.visitor' = 'chat.widget.visitor',
  'availability.org' = 'availability.org',
}

export interface SubscribeUnsubscribeData {
  channelType: PubsubChannelType;
  channelIdentifier: string;
}

export interface SubscribeUnsubscribeDataList {
  channels: Array<SubscribeUnsubscribeData>;
}

export interface ChatTypingUpdate {
  isTyping: boolean;
  isAI: boolean;
  conversationId: number;
  agent: Agent;
}

export interface ChatTypingVisitorUpdate {
  isTyping: boolean;
  conversationId: number;
}

export interface ReadChatMessages {
  chatMessageIds: string[];
}

export type VisitorRelayData = {
  type: 'rrweb-reset';
};

export type StartJourneyResponse = {
  orgHasTimeOnPageEntryConditions?: boolean;
  visitorJourney?: VisitorJourney;
  testResult?: JourneyTestResult;
  explain: string;
};

export type CallEndedDetailsPayload = {
  session: VisitorSession;
  visitorInteraction: SessionInteraction;
  visitor: UniqueVisitor;
};

/** Data from the server */
export interface ServerTransport {
  Widget: {
    // On events
    Init: WidgetInitData;
    Update: WidgetVisitor;
    Availability: Pick<WidgetOrg, 'available' | 'busy'>;
    ChatMessage: ChatMessage;
    ChatTyping: ChatTypingUpdate;
    RrwebEventAcks: { acks: RrwebEventAcksSerializeable };
    PubsubMessage: SubscribeUnsubscribeData & {
      message: any;
    };
    VisitorRelay: VisitorRelayData;
    IntroductionMessage: { message: string };
    // Action event responses
    Introduction: {
      sid: string;
      initData: WidgetInitData;
      update: Visitor;
      sbid?: string;
      conversations: Array<PartialConversation>;
    };
    PostIntroduction: {
      timeOnPageValues: number[];
    };
    Identity: { sbid: string };
    JourneyTest: { testResult: JourneyTestResult; timestamp: number };
    ChatMessageResponse: { data: ChatMessage } | { error: ChatMessageErrorCode };
    WebRtcConnection: {
      token: string;
      room: string;
      agentId: number;
    };
    StartJourneyResponse: StartJourneyResponse;
    WidgetSettingsUpdate: any;
    TestToken: {
      token: string;
    };
    AiGreetingRequest: { data: string };
    AiUpdateAvatarSessionId: { heygenSessionId?: string; conversationId: number };
    AiCobrowsingAction: AiCobrowsingAction;
    AiDemoSetup: AiDemoSession;
    AiDemoAdvance: AiDemoSession;
  };
  Frontend: {
    // On events
    VisitorUpdates: VisitorUpdates;
    VisitorsSync: { visitors: PartialVisitor[] };
    VisitorsCount: { total: number; streams: Record<number, number> };
    UpdateUsage: {
      remainingProactiveCalls: null | number;
      remainingReactiveCalls: null | number;
      remainingTakeovers: null | number;
      remainingSpyViewings: null | number;
      remainingSixsenseCompanyDetailFetches: null | number;
    };
    Availability: { available: boolean; admins: number[] };
    BuildInformation: { commitHash: string; protocolVersion: number };
    RrwebEvent: { data: ServerRrwebData };
    RrwebSnapshotTicket: { ticket: string };
    ChatTyping: ChatTypingVisitorUpdate;
    CallEndedDetails: CallEndedDetailsPayload;
    DestroyActivePeer: { message: string; code: 'CALL_LIMIT' | 'ADMIN_KICK' };

    // Action event responses
    Introduction: {
      sid: string;
      visitorsSync: { visitors: Visitor[] };
      visitorsCount: { total: number; streams: Record<number, number> };
      buildInformation: { commitHash: string; protocolVersion: number };
      unreadInboxMessageCount: number;
    };
    AssociateVisitor: {};
    DissociateVisitor: {};
    UpdateVisitorInteraction: { data: Visitor };
    ChatMessageResponse: { data: ChatMessage };
    PubsubMessage: SubscribeUnsubscribeData & {
      message: any;
    };
    WebRtcConnection: {
      token: string;
      room: string;
      agentId: number;
    };
    SalesfloorChatMessageResponse: { data: SalesfloorChatMessage };
  };
}

/** Data from frontend */
export interface FrontendTransport {
  Server: {
    AssociateVisitor: {
      sbid: string;
    };
    DissociateVisitor: {
      sbid: string;
    };
    UpdateVisitorInteraction: VisitorInteractionUpdate;
    KickAgent: {
      agentId: number;
      visitorId: string;
    };
    ChatMessage: ChatMessageArgs;
    ChatTyping: ChatTypingUpdate;
    ReadChatMessages: ReadChatMessages;
    RrwebEventAcks: {
      visitorSid: string;
      acks: RrwebEventAcksSerializeable;
    };
    Subscribe: SubscribeUnsubscribeData;
    Unsubscribe: SubscribeUnsubscribeData;
    VisitorRelay: {
      visitorSid: string;
      data: VisitorRelayData;
    };
    ResetSpyOptin: {
      sbid: string;
    };
    SalesfloorChatMessage: SalesfloorChatMessage;
  };
}

export type NetworkStats = {
  type?: string;
  effectiveType?: string;
  downlink?: number;
  downlinkMax?: number;
  rtt?: number;
};
export type WidgetIntroductionData = {
  sbid: string;
  url: string;
  urlTitle: string;
  referrer: string;
  userAgent: string;
  isPageVisible: boolean;
  networkStats?: NetworkStats;
  commitHash?: string;
  deviceType: 'mobile' | 'tablet' | 'desktop';
  test?: {
    token: string;
    journeyId?: number;
    impersonateEmail?: string;
    impersonateDomain?: string;
    forceJourneyExecution?: boolean;
  };
};

export interface WidgetAlert {
  title?: string;
  body?: string;
}

export interface WidgetRrwebData {
  agentSid: string;
  content: Chunk;
}

/** Data from widget */
export interface WidgetTransport {
  Server: {
    Introduction: WidgetIntroductionData;
    VisitorUpdate: Partial<WidgetVisitor>;
    Alert: WidgetAlert;
    Identity: VisitorIdentity;
    ChatMessage: ChatMessageArgs;
    ChatTyping: ChatTypingVisitorUpdate;
    ReadChatMessages: ReadChatMessages;
    RrwebEvent: WidgetRrwebData;
    Subscribe: SubscribeUnsubscribeData | SubscribeUnsubscribeDataList;
    Unsubscribe: SubscribeUnsubscribeData;
    SalesfloorChatMessage: SalesfloorChatMessage;
    AiCobrowsingMessage: AiCobrowsingMessageArgs;
    AiDemoSetup: { conversationId: number };
    AiDemoDelete: { conversationId: number };
    AiDemoAdvance: { conversationId: number };
  };
}

// Type-narrowing helpers

export const isChatTypingVisitorUpdate = (x: any): x is ChatTypingVisitorUpdate => {
  return 'isTyping' in x && 'conversationId' in x && !('agent' in x);
};
